import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverHeader,
	PopoverBody,
	PopoverCloseButton,
	VStack,
	HStack,
	Text,
	Badge,
	Button,
	Box,
	Heading,
	Icon,
	Flex,
	Spinner,
	Divider,
	useColorModeValue,
} from '@chakra-ui/react';
import { FaCalendarAlt, FaExclamationTriangle, FaBellSlash } from 'react-icons/fa';
import {
	formatFollowUpTime,
	useFollowUpAlerts,
} from '../../hooks/useFollowUpAlerts';
import brandColor from '../../styles/Brand';
import HeaderToolbarIconButton, {
	HeaderToolbarBadge,
} from './HeaderToolbarIconButton';

const FollowUpSection = ({ title, icon, colorScheme, items, onLeadClick, renderMeta }) => (
	<Box>
		<HStack spacing={2} mb={2}>
			<Icon as={icon} color={`${colorScheme}.500`} boxSize={3.5} />
			<Text fontSize='xs' fontWeight='bold' color={`${colorScheme}.700`} mb={0}>
				{title}
			</Text>
		</HStack>
		<VStack spacing={2} align='stretch'>
			{items.map(({ lead, followUps, count }) => (
				<Box
					key={lead._id}
					p={2.5}
					borderRadius='lg'
					border='1px solid'
					borderColor='gray.100'
					bg='white'
					cursor='pointer'
					_hover={{ bg: 'gray.50', borderColor: `${colorScheme}.200` }}
					onClick={() => onLeadClick(lead._id)}
					transition='all 0.15s ease'>
					<Flex justify='space-between' align='flex-start' gap={2}>
						<Box minW={0} flex='1'>
							<Text fontSize='sm' fontWeight='semibold' noOfLines={1} mb={0}>
								{lead.leadName || 'Unnamed lead'}
							</Text>
							{renderMeta({ lead, followUps, count })}
						</Box>
						<Badge colorScheme={colorScheme} borderRadius='full' fontSize='10px' flexShrink={0}>
							{count ?? followUps?.length ?? 0}
						</Badge>
					</Flex>
				</Box>
			))}
		</VStack>
	</Box>
);

const FollowUpHeaderAlert = ({ size = 'sm' }) => {
	const navigate = useNavigate();
	const {
		upcoming,
		overdue,
		loading,
		hasAlerts,
		hasOverdue,
		hasUpcoming,
		totalOverdueFUs,
		totalUpcomingFUs,
		totalCount,
		snooze,
	} = useFollowUpAlerts();

	const panelBg = useColorModeValue(brandColor.white, brandColor.darkGray);
	const mutedColor = useColorModeValue('gray.500', 'gray.400');

	if (!hasAlerts) {
		return null;
	}

	const goToLead = (leadId, closePopover) => {
		closePopover();
		navigate(`/leads/${leadId}`);
	};

	return (
		<Popover placement='bottom-end'>
			{({ onClose: closePopover }) => (
				<>
					<PopoverTrigger>
						<HeaderToolbarIconButton
							aria-label='Lead follow-ups'
							icon={<FaCalendarAlt />}
							size={size}
							tone={hasOverdue ? 'alert' : 'default'}
							badge={
								totalCount > 0 ? (
									<HeaderToolbarBadge
										colorScheme={hasOverdue ? 'red' : 'orange'}
										top='-2px'
										right='-2px'>
										{totalCount > 99 ? '99+' : totalCount}
									</HeaderToolbarBadge>
								) : null
							}
						/>
					</PopoverTrigger>
					<PopoverContent
						w='400px'
						maxW='95vw'
						bg={panelBg}
						border='1px solid'
						borderColor='gray.100'
						borderRadius='xl'
						boxShadow='0 16px 40px rgba(0,0,0,0.1)'
						overflow='hidden'
						_focus={{ boxShadow: '0 16px 40px rgba(0,0,0,0.12)' }}>
						<PopoverHeader
							bg='linear-gradient(135deg, rgba(251,146,60,0.12) 0%, rgba(239,68,68,0.08) 100%)'
							borderBottomWidth='1px'
							borderColor='gray.100'
							py={3}
							px={4}>
							<HStack justify='space-between' align='center' pr={6}>
								<HStack spacing={2}>
									<Box
										w='8px'
										h='8px'
										bg={hasOverdue ? 'red.400' : 'orange.400'}
										borderRadius='full'
										boxShadow={`0 0 8px ${hasOverdue ? '#f87171' : '#fb923c'}`}
									/>
									<Heading size='sm' fontWeight='600' mb={0}>
										Follow-ups
									</Heading>
								</HStack>
								<HStack spacing={1}>
									{hasOverdue && (
										<Badge colorScheme='red' borderRadius='full' px={2}>
											{totalOverdueFUs} overdue
										</Badge>
									)}
									{hasUpcoming && (
										<Badge colorScheme='orange' borderRadius='full' px={2}>
											{totalUpcomingFUs} soon
										</Badge>
									)}
								</HStack>
							</HStack>
							<PopoverCloseButton top={2} right={2} />
						</PopoverHeader>
						<PopoverBody p={0}>
							<VStack spacing={4} align='stretch' maxH='420px' overflowY='auto' p={4}>
								{loading ? (
									<Flex py={8} justify='center'>
										<Spinner size='md' color='teal.500' />
									</Flex>
								) : (
									<>
										{hasOverdue && (
											<FollowUpSection
												title={`${totalOverdueFUs} overdue`}
												icon={FaExclamationTriangle}
												colorScheme='red'
												items={overdue}
												onLeadClick={(id) => goToLead(id, closePopover)}
												renderMeta={({ followUps }) => (
													<Text fontSize='xs' color={mutedColor} noOfLines={2} mb={0} mt={0.5}>
														{followUps
															.slice(0, 2)
															.map((f) => f.title)
															.join(' · ')}
													</Text>
												)}
											/>
										)}
										{hasOverdue && hasUpcoming && <Divider />}
										{hasUpcoming && (
											<FollowUpSection
												title={`${totalUpcomingFUs} due within 1 hour`}
												icon={FaCalendarAlt}
												colorScheme='orange'
												items={upcoming}
												onLeadClick={(id) => goToLead(id, closePopover)}
												renderMeta={({ followUps }) => (
													<Text fontSize='xs' color={mutedColor} noOfLines={2} mb={0} mt={0.5}>
														{followUps
															.map((f) => `${f.title} @ ${formatFollowUpTime(f.dateTime)}`)
															.join(' · ')}
													</Text>
												)}
											/>
										)}
									</>
								)}
							</VStack>
							<Box
								px={4}
								py={3}
								borderTop='1px solid'
								borderColor='gray.100'
								bg='gray.50'>
								<HStack spacing={2}>
									<Button
										size='sm'
										colorScheme='teal'
										flex={1}
										onClick={() => {
											closePopover();
											navigate('/leads?attention=1');
										}}>
										View all
									</Button>
									<Button
										size='sm'
										variant='ghost'
										leftIcon={<FaBellSlash />}
										onClick={() => {
											snooze();
											closePopover();
										}}>
										Snooze 1h
									</Button>
								</HStack>
							</Box>
						</PopoverBody>
					</PopoverContent>
				</>
			)}
		</Popover>
	);
};

export default FollowUpHeaderAlert;
